import type { UnifiedCandidate } from '@/hooks/use-smart-search'
import { matchBand } from '@/lib/match-bands'

/** Canonical candidate score: live stream value first, then persisted match payload. */
export function candidateScore(candidate: UnifiedCandidate): number {
  return candidate.score ?? candidate.full?.final_score ?? 0
}

function hasScore(candidate: UnifiedCandidate): boolean {
  return typeof (candidate.score ?? candidate.full?.final_score) === 'number'
}

/** First three candidates by rank (ascending); preserves sort order when ranks tie. */
export function topRankedCandidates(candidates: UnifiedCandidate[]): UnifiedCandidate[] {
  const withRank = candidates.filter((c) => (c.rank ?? c.full?.rank) != null)
  if (withRank.length === 0) {
    return candidates.slice(0, 3)
  }
  const sorted = [...withRank].sort(
    (a, b) => (a.rank ?? a.full?.rank ?? 999) - (b.rank ?? b.full?.rank ?? 999),
  )
  return sorted.slice(0, 3)
}

export function excludeProfileIds(
  candidates: UnifiedCandidate[],
  excludeIds: Set<string>,
): UnifiedCandidate[] {
  if (excludeIds.size === 0) return candidates
  return candidates.filter((c) => !excludeIds.has(c.profile_id))
}

export type LeaderboardCut = {
  candidates: UnifiedCandidate[]
  /** Strong-band candidates among those shown — below `candidates.length` when filler rows were needed. */
  strongCount: number
}

/**
 * Job-report leaderboard cut: every strong-band candidate up to `cap`, floored at
 * `floor` rows by topping up with the next-best scorers regardless of band.
 * Expansion beyond the floor stays strong-only, so more rows always means more
 * genuinely strong candidates; band badges keep filler rows honest.
 */
export function leaderboardCandidates(
  candidates: UnifiedCandidate[],
  { floor = 3, cap = 10 }: { floor?: number; cap?: number } = {},
): LeaderboardCut {
  const scored = candidates
    .filter(hasScore)
    .sort((a, b) => candidateScore(b) - candidateScore(a))
  const strong = scored.filter((candidate) => matchBand(candidateScore(candidate)) === 'strong')
  const result = strong.slice(0, cap)
  const strongCount = result.length
  if (result.length < floor) {
    const shown = new Set(result.map((candidate) => candidate.profile_id))
    for (const candidate of scored) {
      if (result.length >= floor) break
      if (shown.has(candidate.profile_id)) continue
      shown.add(candidate.profile_id)
      result.push(candidate)
    }
  }
  return { candidates: result, strongCount }
}
